// src/routes/marketplace/marketplace-unclaimed-routes.tsx
import { lazy } from 'react';
import type { RoutesProps } from '../routes';
import {
    FaSearch,
    FaGlobe,
    FaArchive,
    FaClipboardList,
    FaGavel,
    FaShieldAlt,
    FaUserPlus,
} from 'react-icons/fa';

// Unclaimed content components
const UnclaimedContentBrowser = lazy(() => import('@/pages/marketplace/unclaimed-content/UnclaimedContentBrowser'));
const MyClaimRequests = lazy(() => import('@/pages/marketplace/unclaimed-content/MyClaimRequests'));
const ClaimReviewDashboard = lazy(() => import('@/pages/marketplace/unclaimed-content/admin/ClaimReviewDashboard'));
const ContentVerification = lazy(() => import('@/pages/marketplace/unclaimed-content/ContentVerification'));

// Unclaimed content routes (Author Finder)
export const marketplaceUnclaimedRoutes: RoutesProps[] = [
    {
        path: '/marketplace/unclaimed',
        name: 'Tìm tác giả',
        element: <UnclaimedContentBrowser />,
        roles: ['right_owner', 'licensee', 'admin', 'guest'],
        icon: FaSearch,
    },
    {
        path: '/marketplace/unclaimed/vault',
        name: 'Kho nội dung Vietrro',
        element: <UnclaimedContentBrowser initialSource="vietrro_vault" showStats={true} allowClaiming={true} />,
        roles: ['right_owner', 'admin', 'staff'],
        icon: FaArchive,
    },
    {
        path: '/marketplace/unclaimed/orphan',
        name: 'Nội dung mồ côi',
        element: <UnclaimedContentBrowser initialSource="orphan_internet" showStats={true} allowClaiming={true} />,
        roles: ['right_owner', 'admin', 'monitor', 'legal'],
        icon: FaGlobe,
    },
    {
        path: '/marketplace/unclaimed/my-claims',
        name: 'Yêu cầu claim của tôi',
        element: <MyClaimRequests />,
        roles: ['right_owner', 'licensee'],
        icon: FaClipboardList,
    },
    {
        path: '/marketplace/unclaimed/admin/review',
        name: 'Xem xét claim',
        element: <ClaimReviewDashboard />,
        roles: ['admin', 'staff', 'legal', 'support_specialist'],
        icon: FaGavel,
    },
    {
        path: '/marketplace/verify',
        name: 'Xác minh quyền tác giả',
        element: <ContentVerification />,
        roles: ['right_owner', 'licensee', 'admin', 'guest'],
        icon: FaShieldAlt,
    },
];

// Navigation structure for unclaimed content (Author Finder)
export const marketplaceUnclaimedNavigationStructure = {
    unclaimedBrowse: {
        key: 'unclaimed-browse',
        translationKey: 'marketplace.unclaimedBrowse',
        path: '/marketplace/unclaimed',
        icon: FaSearch,
        roles: ['right_owner', 'licensee', 'admin', 'guest'],
    },
    vaultContent: {
        key: 'vault-content',
        translationKey: 'marketplace.vaultContent',
        path: '/marketplace/unclaimed/vault',
        icon: FaArchive,
        roles: ['right_owner', 'admin', 'staff'],
    },
    orphanContent: {
        key: 'orphan-content',
        translationKey: 'marketplace.orphanContent',
        path: '/marketplace/unclaimed/orphan',
        icon: FaGlobe,
        roles: ['right_owner', 'admin', 'monitor', 'legal'],
    },
    myClaims: {
        key: 'my-claims',
        translationKey: 'marketplace.myClaims',
        path: '/marketplace/unclaimed/my-claims',
        icon: FaClipboardList,
        roles: ['right_owner', 'licensee'],
    },
    claimReview: {
        key: 'claim-review',
        translationKey: 'marketplace.claimReview',
        path: '/marketplace/unclaimed/admin/review',
        icon: FaGavel,
        roles: ['admin', 'staff', 'legal', 'support_specialist'],
    },
    verification: {
        key: 'content-verification',
        translationKey: 'marketplace.verification',
        path: '/marketplace/verify',
        icon: FaShieldAlt,
        roles: ['right_owner', 'licensee', 'admin', 'guest'],
    },
};

// Helper functions
export const canAccessUnclaimedContent = (userRoles: string[]): boolean => {
    const allowedRoles = ['right_owner', 'licensee', 'admin', 'staff', 'legal', 'monitor', 'guest'];
    return userRoles.some(role => allowedRoles.includes(role));
};

export const canAccessVaultContent = (userRoles: string[]): boolean => {
    const allowedRoles = ['right_owner', 'admin', 'staff'];
    return userRoles.some(role => allowedRoles.includes(role));
};

export const canAccessOrphanContent = (userRoles: string[]): boolean => {
    const allowedRoles = ['right_owner', 'admin', 'monitor', 'legal'];
    return userRoles.some(role => allowedRoles.includes(role));
};

export const canSubmitClaims = (userRoles: string[]): boolean => {
    const allowedRoles = ['right_owner', 'licensee'];
    return userRoles.some(role => allowedRoles.includes(role));
};

export const canReviewClaims = (userRoles: string[]): boolean => {
    const reviewRoles = ['admin', 'staff', 'legal', 'support_specialist'];
    return userRoles.some(role => reviewRoles.includes(role));
};

export const getAuthorFinderMenuGroup = (userRoles: string[]) => {
    if (!canAccessUnclaimedContent(userRoles)) return null;

    const menuItems = [marketplaceUnclaimedNavigationStructure.unclaimedBrowse];
    
    if (canAccessVaultContent(userRoles)) {
        menuItems.push(marketplaceUnclaimedNavigationStructure.vaultContent);
    }
    
    if (canAccessOrphanContent(userRoles)) {
        menuItems.push(marketplaceUnclaimedNavigationStructure.orphanContent);
    }
    
    if (canSubmitClaims(userRoles)) {
        menuItems.push(marketplaceUnclaimedNavigationStructure.myClaims);
    }
    
    if (canReviewClaims(userRoles)) {
        menuItems.push(marketplaceUnclaimedNavigationStructure.claimReview);
    }

    menuItems.push(marketplaceUnclaimedNavigationStructure.verification);

    return {
        key: 'marketplace-author-finder',
        translationKey: 'marketplace.authorFinder',
        icon: FaUserPlus,
        children: menuItems
    };
};

export default marketplaceUnclaimedRoutes;